import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Eye, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import PageLayout from '@/layouts/PageLayout';
import { getAuditTrail } from '@/services/auditTrailService';

const ITEMS_PER_PAGE = 10;

// Configurable: which description prefixes to show in the audit trail.
// Each entry maps to the leading word in the description generated by auditLogger.js.
// 'Returned' = return to requestor (review step); 'Released' = final approval.
const VISIBLE_ACTIONS = [
  // Document/CC/QE action prefixes
  'Created', 'Submitted', 'Approved', 'Released', 'Rejected', 'Returned',
  'Clicked',
  // User actions
  'Updated user', 'Disabled user', 'Enabled user', 'Created user',
];

const formatLocalTime = (unixSeconds) => {
  if (!unixSeconds) return 'N/A';
  const d = new Date(unixSeconds * 1000);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

const AuditTrailPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ entries: [], pagination: { page: 1, limit: ITEMS_PER_PAGE, total: 0 } });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef(null);

  // Modal state for viewing raw request body (user APIs only)
  const [modalEntry, setModalEntry] = useState(null);

  const handleViewSnapshot = (entry) => {
    if (!entry.snapshot) return;
    // User API audit logs have type 'User' — show raw body in modal
    if (entry.type === 'User') {
      setModalEntry(entry);
    } else {
      // Navigate to the entity's detail page in snapshot mode.
      // Use ?snapshot={auditLogId} so the URL is addressable: refresh/copy/paste keeps the snapshot.
      const entityId = entry.entityId || entry.snapshot.id;
      const detailRoutes = {
        Document: 'document',
        'Change Control': 'control',
        Control: 'control',
        QualityEvent: 'quality',
        'Quality Event': 'quality',
      };
      const detailRoute = detailRoutes[entry.type];

      if (!detailRoute || !entityId) {
        setModalEntry(entry);
        return;
      }

      navigate(`/${detailRoute}/${entityId}?snapshot=${entry.id}`);
    }
  };

  const handleCloseModal = () => {
    setModalEntry(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300);
  };

  useEffect(() => {
    const params = { page: currentPage, limit: ITEMS_PER_PAGE, actions: VISIBLE_ACTIONS.join(',') };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    getAuditTrail(params).then(setData).catch(console.error);
  }, [currentPage, debouncedSearch]);

  const totalPages = Math.ceil(data.pagination.total / ITEMS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <PageLayout title="System Audit Trail">
      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder="Search across all fields..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Date/Time</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[160px]">Type</TableHead>
              <TableHead className="w-[120px]">User</TableHead>
              <TableHead className="w-[120px]">Recipient</TableHead>
              <TableHead className="w-[80px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="whitespace-nowrap text-sm">
                  {formatLocalTime(entry.timestamp)}
                </TableCell>
                <TableCell className="max-w-xs truncate text-sm">
                  {entry.description}
                </TableCell>
                <TableCell className="text-sm">{entry.type}</TableCell>
                <TableCell className="text-sm">{entry.user ?? 'Unknown'}</TableCell>
                <TableCell className="text-sm">
                  {entry.recipient ?? 'N/A'}
                </TableCell>
                <TableCell>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleViewSnapshot(entry)}
                    disabled={!entry.snapshot}
                  >
                    <Eye className="mr-1 h-3 w-3" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty states */}
        {data.entries.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {debouncedSearch.trim() ? 'No results found' : 'No Data'}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between space-x-2 px-4 py-4">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({data.pagination.total} entries)
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal for viewing raw request body (User API only) */}
      {modalEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-full max-w-2xl rounded-lg border bg-white p-6 shadow-lg">
            <button
              onClick={handleCloseModal}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="mb-4 text-lg font-semibold">Audit Log Details</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Description:</span>
                <p className="text-sm">{modalEntry.description}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Type:</span>
                <p className="text-sm">{modalEntry.type}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">User:</span>
                <p className="text-sm">{modalEntry.user ?? 'Unknown'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Raw Request Body:</span>
                <pre className="mt-1 max-h-64 overflow-auto rounded bg-muted p-3 text-xs">
                  {JSON.stringify(modalEntry.snapshot, null, 2)}
                </pre>
              </div>
              {modalEntry.comments && modalEntry.comments.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Comments:</span>
                  <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-3 text-xs">
                    {JSON.stringify(modalEntry.comments, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AuditTrailPage;
